import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Wrench } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { num } from "@/lib/format";
import { Panel } from "@/components/common";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

function KoreksiSemua({ rows, onDone }) {
  const { isEditor } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!isEditor) return null;
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/rekonsiliasi/koreksi-semua", {}); toast.success(`${data.jumlah_dikoreksi} KOREKSI SALDO_AWAL DIBUAT (${num(data.total_pcs)} PCS)`); if (data.gagal.length) toast.error(`${data.gagal.length} GAGAL`); setOpen(false); onDone(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded bg-slate-900 text-white" data-testid="rekon-all-btn"><Wrench className="w-3.5 h-3.5" />Koreksi Semua</button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="rekon-all-dialog">
          <AlertDialogHeader><AlertDialogTitle>Koreksi semua {rows.length} baris saldo negatif?</AlertDialogTitle>
            <AlertDialogDescription className="no-up">
              Sistem membuat <b>1 transaksi SALDO_AWAL</b> (dihitung sebagai AMBIL awal) per baris worker×produk, status CONFIRMED, tanggal hari ini. Transaksi lama <b>tidak diubah</b> (non-destruktif, tercatat di audit log).
              <ul className="mt-2 space-y-0.5 max-h-40 overflow-auto">{rows.map((r) => <li key={r.worker_id + r.product_id} className="mono text-xs">{r.worker_id} · {r.product_id} → +{num(r.selisih_rekonsiliasi)} PCS</li>)}</ul>
            </AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="rekon-all-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={go} disabled={busy} className="bg-slate-900" data-testid="rekon-all-confirm">{busy ? "Memproses…" : `Ya, koreksi ${rows.length} baris`}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function RekonsiliasiList() {
  const [d, setD] = useState(null);
  const load = () => api.get("/rekonsiliasi").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d || d.rows.length === 0) return null;
  return (
    <Panel className="mb-6 border-rose-300" testId="rekon-panel" action={<KoreksiSemua rows={d.rows} onDone={load} />}
      title={<span className="text-rose-800 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />PERLU REKONSILIASI — {d.jumlah_worker} WORKER · {num(d.total_selisih)} PCS SETOR MELEBIHI AMBIL</span>}>
      <div className="text-xs text-slate-500 mb-2">Saldo historis negatif per worker per produk. Transaksi sumber tidak diubah — periksa manual atau buat koreksi SALDO_AWAL dari PANGGIL ID.</div>
      <table className="w-full text-sm"><thead><tr className="text-xs text-slate-500 border-b"><th className="px-2 py-1.5 text-left">Worker</th><th className="px-2 py-1.5 text-left">Produk</th><th className="px-2 py-1.5 text-right">Σ Ambil</th><th className="px-2 py-1.5 text-right">Σ Setor</th><th className="px-2 py-1.5 text-right">Selisih</th><th className="px-2 py-1.5"></th></tr></thead>
        <tbody>{d.rows.map((r) => <tr key={r.worker_id + r.product_id} className="border-b border-slate-100" data-testid={`rekon-row-${r.worker_id}-${r.product_id}`}>
          <td className="px-2 py-1.5"><span className="mono font-semibold">{r.worker_id}</span> <span className="data-raw">{r.worker_name}</span></td>
          <td className="px-2 py-1.5 data-raw">{r.product_name} <span className="mono text-xs text-slate-400">{r.product_id}</span></td>
          <td className="px-2 py-1.5 mono text-right">{num(r.ambil)}</td><td className="px-2 py-1.5 mono text-right">{num(r.setor)}</td>
          <td className="px-2 py-1.5 mono text-right font-bold text-rose-700">{num(r.selisih_rekonsiliasi)} PCS</td>
          <td className="px-2 py-1.5 text-right"><Link to="/panggil" state={{ wid: r.worker_id }} className="text-xs text-blue-700 underline" data-testid={`rekon-open-${r.worker_id}`}>Buka PANGGIL ID →</Link></td>
        </tr>)}</tbody></table>
    </Panel>
  );
}
